import { Platform } from 'react-native';

import { Button, Text as FreshText } from '@fhs/ui';
import { Box, Text } from '@fhs-legacy/universal-components';
import { IRestaurantNode } from 'generated/rbi-graphql';

import { webFocusableSectionTextProps } from '../../../utils/web-named-region';

const SuggestionBox = Box.withConfig({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  paddingY: 2,
});

const InnerBox = Box.withConfig({
  flex: 1,
  display: 'flex',
  alignItems: 'start',
  alignContent: 'start',
  flexDirection: 'column',
  justifyItems: 'start',
  justifyContent: 'center',
});

const ButtonBox = Box.withConfig({
  display: 'flex',
});

export const PickUpAt = Text.withConfig({
  textAlign: 'left',

  fontSize: 'xs',
  lineHeight: 'xs',
  fontFamily: 'body',
});

export const DistanceAndAddress = Text.withConfig({
  ellipsizeMode: 'tail',
  numberOfLines: 1,
  textAlign: 'left',

  fontSize: 'xs',
  lineHeight: 'xs',
  fontFamily: 'body',
});

export type AddressBoxSuggestionProps = {
  pickStoreAction: () => void;
  distance: string;
  restaurant: IRestaurantNode;
};

const addressFields = ['address1', 'address2', 'city', 'state', 'country', 'zip'] as const;

function getRestaurantAddress(store: IRestaurantNode) {
  const addressField = store.physicalAddress ?? {};

  return addressFields
    .map(field => addressField[field])
    .filter(Boolean)
    .join(', ');
}

function buildRestaurantSummaryLabel(storeName: string, distance: string, address: string) {
  return `Pick Up: ${storeName}. ${distance} · ${address}`;
}

export const AddressBoxSuggestion: React.FC<React.PropsWithChildren<AddressBoxSuggestionProps>> = ({
  pickStoreAction,
  distance,
  restaurant,
}) => {
  const storeName = restaurant?.name ?? '';
  const address = getRestaurantAddress(restaurant);
  const restaurantSummaryLabel = buildRestaurantSummaryLabel(storeName, distance, address);

  const detailsFocusProps =
    Platform.OS === 'web'
      ? webFocusableSectionTextProps(restaurantSummaryLabel)
      : {
          accessible: true as const,
          accessibilityRole: 'text' as const,
          accessibilityLabel: restaurantSummaryLabel,
        };

  const handleSelectStore = () => {
    pickStoreAction();
  };

  return (
    <SuggestionBox>
      <InnerBox {...detailsFocusProps}>
        <PickUpAt accessible={false}>Pick Up:</PickUpAt>
        <FreshText.Heading type="four" accessible={false}>
          {storeName}
        </FreshText.Heading>
        <DistanceAndAddress accessible={false}>{`${distance} · ${address}`}</DistanceAndAddress>
      </InnerBox>
      <ButtonBox>
        <Button
          type="outline"
          size="xs"
          testID={`store-suggestion-select-${storeName}`}
          accessibilityLabel={`Select ${storeName}`}
          onPress={handleSelectStore}
        >
          <Button.Text>Select</Button.Text>
        </Button>
      </ButtonBox>
    </SuggestionBox>
  );
};
